import {
  PointOfInterest,
  PointOfInterestTemplate,
  useUpdatePointOfInterestMutation,
} from '@/__generated__/graphql';
import { DEFAULT_REVIEW_STATUS, POI_LINK } from '@/constants';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useMemo } from 'react';
import { PillMenu } from '@/components/shared/PillMenu';
import { PillStatus } from '@/components/shared/Pill/types';
import { useReviewIn3D } from '../../../hooks/useReviewIn3D';

const getPillStatus = (status: string): PillStatus => {
  switch (status.trim()) {
    case 'Pending review':
      return 'info';
    case 'GVI':
      return 'failed';
    case 'CVI':
      return 'warning';
    case 'No Follow Up':
      return 'success';
    default:
      return 'info';
  }
};

export const ReviewAction = ({
  pointOfInterest,
  templateOfSelectedPoi,
  location = 'review-panel-poi',
}: {
  pointOfInterest: PointOfInterest;
  templateOfSelectedPoi: Partial<PointOfInterestTemplate> | undefined;
  location?: string;
}) => {
  const [updateMutation, { loading }] = useUpdatePointOfInterestMutation();
  const { showSnackBar } = useSnackBarMessage({});

  const actionField = useMemo(() => {
    if (!templateOfSelectedPoi?.fields) return null;
    return templateOfSelectedPoi?.fields?.find((field) => field.key === 'review_action');
  }, [templateOfSelectedPoi?.fields]);

  const action = useMemo(() => {
    if (!actionField) return DEFAULT_REVIEW_STATUS;
    const stored = pointOfInterest.templateFieldData?.review_action?.trim();
    if (stored) return stored;
    const firstOption = actionField.options?.[0];
    return (typeof firstOption === 'string' ? firstOption : undefined) ?? DEFAULT_REVIEW_STATUS;
  }, [actionField, pointOfInterest.templateFieldData]);

  const { imageLink: generatedPoiLink } = useReviewIn3D({ pointOfInterest });

  if (!actionField) return <></>;

  const handleChange = async (action: string) => {
    const initialTemplateFieldData = (templateOfSelectedPoi?.fields ?? []).reduce((acc, field) => {
      if (field.key) {
        acc[field.key] =
          field.key === POI_LINK
            ? generatedPoiLink ?? pointOfInterest.templateFieldData?.[field.key] ?? ''
            : pointOfInterest.templateFieldData?.[field.key] ?? '';
      }
      return acc;
    }, {} as Record<string, string>);

    const templateFieldData: Record<string, string> = {
      ...initialTemplateFieldData,
      review_action: action.trim(),
    };

    try {
      await updateMutation({
        variables: {
          input: {
            pointOfInterestId: pointOfInterest.id,
            templateId: templateOfSelectedPoi?.id ?? '',
            templateFieldData,
            lastModifiedFor: 'Update Review Action',
          },
        },
        optimisticResponse: {
          updatePointOfInterest: {
            __typename: 'PointOfInterest',
            id: pointOfInterest.id,
          },
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify({ __typename: 'PointOfInterest', id: pointOfInterest.id }),
            fields: {
              templateFieldData: () => templateFieldData,
            },
          });
        },
      });
      showSnackBar('Review action updated successfully', 'success');
    } catch {
      showSnackBar('Failed to update review action. Please contact support', 'error');
    }
  };

  return (
    <PillMenu
      key={`${pointOfInterest.id}`}
      options={actionField.options}
      initialValue={action}
      loading={loading}
      loadingLabel="Updating..."
      onChange={handleChange}
      getPillStatus={getPillStatus}
      className={`${location}-action-pill-menu`}
    />
  );
};
